import { getCountryCallingCode, type CountryCode } from 'libphonenumber-js';

// ─── Types ───

export type CountryIso2 = string;

export interface CountryOption {
  iso2: CountryIso2;
  name: string;
  dialCode: string;
  flag: string;
  label: string;
  searchText: string;
}

export interface CountryPickerConfig {
  countries: CountryIso2[];
  defaultCountry: CountryIso2;
}

// ─── SEA default preset ───

export const SEA_COUNTRIES: string[] = ['PH', 'SG', 'MY', 'TH', 'ID', 'VN'];
export const DEFAULT_COUNTRY: string = 'PH';

// ─── Utilities ───

/** Convert ISO-2 country code to emoji flag. */
export function iso2ToFlagEmoji(iso2: string): string {
  const upper = iso2.toUpperCase();
  if (upper.length !== 2) return iso2;
  const codePoints = [...upper].map((c) => 0x1f1e6 - 65 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

/** Get localized country name using Intl.DisplayNames, fallback to ISO2. */
export function getCountryName(iso2: string, locale?: string): string {
  try {
    const displayNames = new Intl.DisplayNames([locale ?? navigator.language], { type: 'region' });
    return displayNames.of(iso2.toUpperCase()) ?? iso2.toUpperCase();
  } catch {
    return iso2.toUpperCase();
  }
}

/** Get dial code prefixed with +, e.g. "+63". */
export function getDialCode(iso2: string): string {
  try {
    return `+${getCountryCallingCode(iso2.toUpperCase() as CountryCode)}`;
  } catch {
    return '';
  }
}

/** Alias for getDialCode — maps ISO2 to stored dial code format. */
export function iso2ToDialCode(iso2: string): string {
  return getDialCode(iso2);
}

/** Build array of country options for a given list of ISO2 codes. */
export function buildCountryOptions(countries: string[], locale?: string): CountryOption[] {
  return countries.map((iso2) => {
    const upper = iso2.toUpperCase();
    const name = getCountryName(upper, locale);
    const dialCode = getDialCode(upper);
    const flag = iso2ToFlagEmoji(upper);
    const label = `${flag} ${name} (${dialCode})`;
    const searchText = `${name} ${upper} ${dialCode}`.toLowerCase();
    return { iso2: upper, name, dialCode, flag, label, searchText };
  });
}

/** Map a stored dial code (e.g. "+63") back to an ISO2 from the configured options. */
export function dialCodeToIso2(
  dialCode: string | null | undefined,
  options: CountryOption[],
  fallbackIso2: string,
): string {
  if (!dialCode) return fallbackIso2;
  const match = options.find((o) => o.dialCode === dialCode);
  return match ? match.iso2 : fallbackIso2;
}

// ─── Config parser ───

/**
 * Parse the countryPicker config from patient_field_definitions.validation_override.
 * Falls back to SEA preset if missing or invalid.
 */
export function getMobileCountryPickerConfig(
  validationOverrideRaw: string | null | undefined,
): CountryPickerConfig {
  if (!validationOverrideRaw) {
    return { countries: SEA_COUNTRIES, defaultCountry: DEFAULT_COUNTRY };
  }
  try {
    const parsed = JSON.parse(validationOverrideRaw);
    const cp = parsed?.countryPicker;
    if (!cp || !Array.isArray(cp.countries) || cp.countries.length === 0) {
      return { countries: SEA_COUNTRIES, defaultCountry: DEFAULT_COUNTRY };
    }
    const countries = cp.countries.map((c: string) => String(c).toUpperCase());
    const defaultCountry =
      typeof cp.defaultCountry === 'string' && countries.includes(cp.defaultCountry.toUpperCase())
        ? cp.defaultCountry.toUpperCase()
        : countries[0];
    return { countries, defaultCountry };
  } catch {
    return { countries: SEA_COUNTRIES, defaultCountry: DEFAULT_COUNTRY };
  }
}
